/*
 * PER-1218 phase 2 — the chunk streaming core.
 *
 * Owns the chunk manifest, a `ChunkBvh` spatial index over it, the
 * set of currently-resident (loaded-into-scene) chunks, and a
 * distance-based LOD proxy for chunks that are visible but far from
 * the camera. `update()` is meant to be called once per frame (or on
 * camera change) from the render loop; it does NOT touch the worker
 * directly — it calls the caller-supplied `requestChunk` callback and
 * waits for `onChunkBytes` to be called back, which keeps this class
 * testable without a real `Worker`.
 *
 * PER-1230 (second PR 34 review) fixed two production-scale bugs in
 * here: the LOD-far distance is now computed per-manifest, scaled to
 * the loaded file's own size, instead of a fixed 500 ft that silently
 * stopped requesting *any* geometry once a model got wide enough that
 * the auto-framed camera started farther away than that (see
 * `setManifest`/`LOD_FAR_DISTANCE_FRACTION`); and eviction now also
 * fires unconditionally for a resident chunk that has drifted both
 * out of frustum and past that distance, not only when the (in
 * practice unreachable) byte budget is exceeded (see `evict`).
 *
 * # entityIndex is already the global index — no remap needed
 *
 * `modelToGlbChunk` (Rust, `src/ifc/chunking.rs`) builds a filtered
 * clone of `model.entities` containing only the chunk's entities, but
 * passes the chunk's original `entity_indices` through to the glTF
 * builder alongside it (`build_gltf_indexed`), so every node's
 * `extras.entityIndex` (-> `userData.entityIndex` after `GLTFLoader`
 * copies `extras` onto `userData`) in a per-chunk GLB is already the
 * global index into the full `model.entities` array — the same
 * contract the unchunked `modelToGlb` path guarantees. Nothing in
 * `onChunkBytes` needs to translate it before `showElementInfo`/
 * raycasting in `main.ts` use it.
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import {
  MEMORY_BUDGET_BYTES,
  LOD_FAR_DISTANCE_FEET,
  LOD_FAR_DISTANCE_FRACTION,
} from './streaming-config';
import { ChunkBvh } from './chunk-bvh';

/** Mirrors `rvt::ifc::chunking::ChunkManifestEntry` (Rust), as emitted
 * by `serde_wasm_bindgen`'s `json_compatible()` serializer:
 * `id`/`entity_indices` have no `#[serde(rename)]` so they stay
 * snake_case; `aabbMin`/`aabbMax`/`triangleCount` are explicitly
 * renamed to camelCase in the Rust struct. */
export interface ChunkManifestEntry {
  id: number;
  entity_indices: number[];
  aabbMin: [number, number, number];
  aabbMax: [number, number, number];
  triangleCount: number;
}

interface ResidentChunk {
  object3d: THREE.Object3D;
  byteLength: number;
  lastUsedMs: number;
}

export interface ChunkManagerOpts {
  requestChunk: (chunkId: number, entityIndices: number[]) => void;
  /** Injectable clock so eviction/LRU tests are deterministic. Defaults to `performance.now`. */
  now?: () => number;
  /** Injectable memory budget so tests don't need thousands of fake chunks. Defaults to the real 1.5 GB constant. */
  memoryBudgetBytes?: number;
  /**
   * Injectable GLB decoder, defaulting to the real
   * `GLTFLoader` + Blob-URL path. Unit tests (`chunk-manager.spec.ts`)
   * override this to resolve synchronously/deterministically without
   * depending on browser-only APIs (`URL.createObjectURL`, `fetch`
   * over a `blob:` URL) that a plain Node test environment doesn't
   * implement — the override is test-only wiring, never a production
   * code path; production always gets the real GLTFLoader default.
   */
  parseGlb?: (glb: Uint8Array) => Promise<THREE.Object3D>;
}

function defaultParseGlb(glb: Uint8Array): Promise<THREE.Object3D> {
  return new Promise((resolve, reject) => {
    const blob = new Blob([glb.buffer as ArrayBuffer], { type: 'model/gltf-binary' });
    const url = URL.createObjectURL(blob);
    const loader = new GLTFLoader();
    loader.load(
      url,
      (gltf) => {
        URL.revokeObjectURL(url);
        resolve(gltf.scene);
      },
      undefined,
      (err) => {
        URL.revokeObjectURL(url);
        reject(err instanceof Error ? err : new Error(String(err)));
      },
    );
  });
}

function disposeObject3d(obj: THREE.Object3D): void {
  obj.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (mesh.geometry) mesh.geometry.dispose();
    const mat = mesh.material;
    if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
    else if (mat) (mat as THREE.Material).dispose();
  });
}

function nearestDistanceToBox(
  cameraPos: THREE.Vector3,
  min: [number, number, number],
  max: [number, number, number],
): number {
  const box = new THREE.Box3(
    new THREE.Vector3(min[0], min[1], min[2]),
    new THREE.Vector3(max[0], max[1], max[2]),
  );
  const closest = box.clampPoint(cameraPos, new THREE.Vector3());
  return closest.distanceTo(cameraPos);
}

/**
 * PER-1230 blocker 2: what actually costs memory once a chunk is
 * resident is the decoded `three.js` geometry (position/normal/uv/
 * index `TypedArray`s), not the GLB wire bytes it arrived as — the
 * GLB `Uint8Array` this is decoded from is not retained. Summing
 * every `BufferAttribute`/index array's `byteLength` across the
 * decoded object gives an actual lower bound on that cost, instead of
 * a serialized-size figure the eviction budget can never realistically
 * reach.
 */
function decodedGeometryBytes(root: THREE.Object3D): number {
  let total = 0;
  root.traverse((child) => {
    const mesh = child as THREE.Mesh;
    const geometry = mesh.geometry as THREE.BufferGeometry | undefined;
    if (!geometry) return;
    for (const key of Object.keys(geometry.attributes)) {
      total += geometry.attributes[key]!.array.byteLength;
    }
    if (geometry.index) total += geometry.index.array.byteLength;
  });
  return total;
}

export class ChunkManager {
  private manifest: ChunkManifestEntry[] = [];
  private byId = new Map<number, ChunkManifestEntry>();
  private bvh: ChunkBvh | null = null;
  private resident = new Map<number, ResidentChunk>();
  private inFlight = new Set<number>();
  private proxies = new Map<number, THREE.Object3D>();
  private residentBytes = 0;
  private readonly now: () => number;
  private readonly memoryBudgetBytes: number;
  private readonly parseGlb: (glb: Uint8Array) => Promise<THREE.Object3D>;
  /**
   * Per-file LOD-far distance (feet), recomputed by `setManifest` from
   * the manifest's own bounds — see `LOD_FAR_DISTANCE_FRACTION`'s doc
   * comment. Defaults to the fixed floor before any manifest loads.
   */
  private lodFarDistanceFeet: number = LOD_FAR_DISTANCE_FEET;

  constructor(
    private readonly scene: THREE.Scene,
    private readonly camera: THREE.PerspectiveCamera,
    private opts: ChunkManagerOpts,
  ) {
    this.now = opts.now ?? (() => performance.now());
    this.memoryBudgetBytes = opts.memoryBudgetBytes ?? MEMORY_BUDGET_BYTES;
    this.parseGlb = opts.parseGlb ?? defaultParseGlb;
  }

  /** Swap in a new manifest for a newly loaded model, disposing everything the previous model had resident. */
  setManifest(manifest: ChunkManifestEntry[]): void {
    this.disposeAll();
    this.manifest = manifest;
    this.byId = new Map(manifest.map((e) => [e.id, e]));
    this.bvh = new ChunkBvh(manifest.map((e) => ({ id: e.id, min: e.aabbMin, max: e.aabbMax })));
    this.lodFarDistanceFeet = Math.max(LOD_FAR_DISTANCE_FEET, this.manifestMaxDim() * LOD_FAR_DISTANCE_FRACTION);
  }

  /** Largest AABB dimension across the whole manifest, 0 for an empty manifest. */
  private manifestMaxDim(): number {
    const bounds = this.manifestBounds();
    if (!bounds) return 0;
    return Math.max(
      bounds.max[0] - bounds.min[0],
      bounds.max[1] - bounds.min[1],
      bounds.max[2] - bounds.min[2],
    );
  }

  /** Dispose every resident chunk and proxy, and forget in-flight requests. Call on file-close/reset. */
  disposeAll(): void {
    for (const chunk of this.resident.values()) {
      this.scene.remove(chunk.object3d);
      disposeObject3d(chunk.object3d);
    }
    this.resident.clear();
    this.residentBytes = 0;
    for (const proxy of this.proxies.values()) {
      this.scene.remove(proxy);
      disposeObject3d(proxy);
    }
    this.proxies.clear();
    this.inFlight.clear();
    this.manifest = [];
    this.byId.clear();
    this.bvh = null;
  }

  /** Union AABB of the whole manifest, for a cheap initial camera auto-frame before any chunk has streamed in. */
  manifestBounds(): { min: [number, number, number]; max: [number, number, number] } | null {
    if (this.manifest.length === 0) return null;
    let min = this.manifest[0]!.aabbMin;
    let max = this.manifest[0]!.aabbMax;
    for (const e of this.manifest.slice(1)) {
      min = [Math.min(min[0], e.aabbMin[0]), Math.min(min[1], e.aabbMin[1]), Math.min(min[2], e.aabbMin[2])];
      max = [Math.max(max[0], e.aabbMax[0]), Math.max(max[1], e.aabbMax[1]), Math.max(max[2], e.aabbMax[2])];
    }
    return { min, max };
  }

  /** Call once per frame (or on camera change) from the render loop. */
  update(): void {
    if (!this.bvh || this.manifest.length === 0) return;

    this.camera.updateMatrixWorld();
    const frustum = new THREE.Frustum();
    const projScreenMatrix = new THREE.Matrix4().multiplyMatrices(
      this.camera.projectionMatrix,
      this.camera.matrixWorldInverse,
    );
    frustum.setFromProjectionMatrix(projScreenMatrix);
    const box3 = new THREE.Box3();
    const intersects = (min: [number, number, number], max: [number, number, number]): boolean => {
      box3.min.set(min[0], min[1], min[2]);
      box3.max.set(max[0], max[1], max[2]);
      return frustum.intersectsBox(box3);
    };

    const visibleIds = new Set(this.bvh.query(intersects));
    const nowMs = this.now();
    const cameraPos = this.camera.position;

    for (const id of visibleIds) {
      const entry = this.byId.get(id);
      if (!entry) continue;

      const resident = this.resident.get(id);
      if (resident) {
        resident.lastUsedMs = nowMs;
        // Real geometry is loaded; make sure any leftover proxy is gone.
        this.removeProxy(id);
        continue;
      }

      const distance = nearestDistanceToBox(cameraPos, entry.aabbMin, entry.aabbMax);
      if (distance > this.lodFarDistanceFeet) {
        this.ensureProxy(entry);
        // Deliberately do not request the real chunk while far away —
        // the proxy is the intended representation at this distance.
        continue;
      }

      // Within range: request the real chunk if we haven't already,
      // and leave any existing proxy in place until it lands (swapped
      // out in onChunkBytes).
      if (!this.inFlight.has(id)) {
        this.inFlight.add(id);
        this.opts.requestChunk(id, entry.entity_indices);
      }
    }

    // Chunks no longer visible: drop their proxy (cheap, no reason to
    // keep rendering a box for something off-screen); resident real
    // geometry is left alone here — eviction below decides its fate.
    for (const id of this.proxies.keys()) {
      if (!visibleIds.has(id)) this.removeProxy(id);
    }

    this.evict(visibleIds, cameraPos);
  }

  /** Called by main.ts once the worker responds to a `requestChunk`. */
  onChunkBytes(chunkId: number, glb: Uint8Array): void {
    const entry = this.byId.get(chunkId);
    this.inFlight.delete(chunkId);
    if (!entry) return; // manifest was swapped out (new file loaded) while this was in flight.

    void this.parseGlb(glb)
      .then((root) => {
        // The manifest could have been swapped again while this
        // chunk's GLB was decoding asynchronously; don't add stale
        // geometry to a scene that has moved on to a different file.
        if (this.byId.get(chunkId) !== entry) return;

        root.userData.chunkId = chunkId;

        this.scene.add(root);
        const decodedBytes = decodedGeometryBytes(root);
        this.resident.set(chunkId, {
          object3d: root,
          byteLength: decodedBytes,
          lastUsedMs: this.now(),
        });
        this.residentBytes += decodedBytes;
        this.removeProxy(chunkId);
      })
      .catch(() => {
        // Decode failure — nothing added, nothing resident. The next
        // `update()` pass will see the chunk still isn't resident and
        // request it again.
      });
  }

  residentChunkCount(): number {
    return this.resident.size;
  }
  residentBytesTotal(): number {
    return this.residentBytes;
  }
  isResident(chunkId: number): boolean {
    return this.resident.has(chunkId);
  }

  private ensureProxy(entry: ChunkManifestEntry): void {
    if (this.proxies.has(entry.id)) return;
    const size: [number, number, number] = [
      Math.max(entry.aabbMax[0] - entry.aabbMin[0], 0.01),
      Math.max(entry.aabbMax[1] - entry.aabbMin[1], 0.01),
      Math.max(entry.aabbMax[2] - entry.aabbMin[2], 0.01),
    ];
    const center: [number, number, number] = [
      (entry.aabbMin[0] + entry.aabbMax[0]) / 2,
      (entry.aabbMin[1] + entry.aabbMax[1]) / 2,
      (entry.aabbMin[2] + entry.aabbMax[2]) / 2,
    ];
    const geometry = new THREE.BoxGeometry(size[0], size[1], size[2]);
    const material = new THREE.MeshBasicMaterial({
      color: 0x4a5568,
      wireframe: true,
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(center[0], center[1], center[2]);
    mesh.userData.isLodProxy = true;
    mesh.userData.chunkId = entry.id;
    this.scene.add(mesh);
    this.proxies.set(entry.id, mesh);
  }

  private removeProxy(chunkId: number): void {
    const proxy = this.proxies.get(chunkId);
    if (!proxy) return;
    this.scene.remove(proxy);
    disposeObject3d(proxy);
    this.proxies.delete(chunkId);
  }

  private evict(visibleIds: Set<number>, cameraPos: THREE.Vector3): void {
    const outOfFrustum = [...this.resident.entries()].filter(([id]) => !visibleIds.has(id));

    // PER-1230 blocker 2: at production model sizes the byte budget
    // below is essentially never hit (see `MEMORY_BUDGET_BYTES`'s doc
    // comment), so gating eviction solely on it means "memory falls
    // when the camera moves away" was only ever a property of the
    // 10-byte test budget, never of production behavior. A resident
    // chunk that has drifted out of frustum *and* past this file's
    // LOD-far distance is no longer being shown as anything (not even
    // a proxy — proxies only exist for chunks still inside the
    // frustum) and is unconditionally reclaimed here, independent of
    // whether the total is currently over budget.
    for (const [id, chunk] of outOfFrustum) {
      const entry = this.byId.get(id);
      if (!entry) continue;
      const distance = nearestDistanceToBox(cameraPos, entry.aabbMin, entry.aabbMax);
      if (distance <= this.lodFarDistanceFeet) continue;
      this.scene.remove(chunk.object3d);
      disposeObject3d(chunk.object3d);
      this.resident.delete(id);
      this.residentBytes -= chunk.byteLength;
    }

    if (this.residentBytes <= this.memoryBudgetBytes) return;
    // Oldest-lastUsedMs-first among the remaining out-of-frustum
    // chunks (near ones the pass above didn't touch). A chunk the
    // camera can currently see is never evicted even over budget —
    // that would be a visible pop, not a background reclaim.
    const candidates = [...this.resident.entries()]
      .filter(([id]) => !visibleIds.has(id))
      .sort((a, b) => a[1].lastUsedMs - b[1].lastUsedMs);

    for (const [id, chunk] of candidates) {
      if (this.residentBytes <= this.memoryBudgetBytes) break;
      this.scene.remove(chunk.object3d);
      disposeObject3d(chunk.object3d);
      this.resident.delete(id);
      this.residentBytes -= chunk.byteLength;
    }
  }
}
